import { useMemo } from 'react';
import { Check, Clear } from '@mui/icons-material';
import { Box, Divider, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Action, useAdminModelsQuery } from '../../../api/gql';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { ActionIcon } from '../../../components/invoice/ScheduledActionIcon';

const TRANS_NS = 'universal-reminding';

type ModelFieldsOverviewProps = {
  id: string;
  usedFields: {
    email: string[][];
    sms: string[][];
  };
} & PaperProps;
export const ModelFieldsOverview = ({ id, usedFields, sx, ...rest }: ModelFieldsOverviewProps) => {
  const { t } = useTranslation(TRANS_NS, { keyPrefix: 'scenarios.model' });
  const { data, isLoading } = useAdminModelsQuery();

  const selectedModel = useMemo(() => data?.invoiceModels.find((model) => model.id === id), [data, id]);

  const fieldsData = useMemo(
    () =>
      selectedModel?.fields.map((f) => ({
        key: f.name,
        required: f.required,
        name: f.name,
      })),
    [selectedModel],
  );

  return (
    <Paper
      sx={{
        position: 'relative',
        p: 2,
        ...sx,
      }}
      {...rest}
    >
      {isLoading ? <FullSizeLoader /> : null}
      <Typography>{t('variables')}</Typography>
      <Divider />
      <Box mt={2}>
        {!selectedModel ? <Typography color={'text.secondary'}>{t('choose')}</Typography> : null}

        {fieldsData?.length ? (
          <Stack gap={2} direction={'row'} flexWrap={'wrap'} justifyContent={'space-around'}>
            {fieldsData.map((field) => (
              <Box
                key={field.key}
                sx={{
                  minWidth: 190,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'baseline'} gap={1} pb={0.5}>
                  <Typography variant={'body2'} fontWeight={'bold'}>
                    [{field.name}]
                  </Typography>
                  <Typography variant={'caption'} color={'text.secondary'}>
                    {field.required ? t('required') : t('optional')}
                  </Typography>
                </Box>
                <Divider />
                <Stack direction={'row'} gap={1} alignItems={'flex-end'} mt={0.5}>
                  <ActionIcon color={'action'} action={Action.Email} fontSize={'small'} />
                  {usedFields.email.map((email, index) => (
                    <Stack key={index} alignItems={'center'}>
                      <Typography variant={'caption'} color={'text.secondary'}>{`${index + 1}`}</Typography>
                      {email.includes(`[${field.name}]`) ? (
                        <Check fontSize={'small'} color={'success'} />
                      ) : (
                        <Clear fontSize={'small'} color={'error'} />
                      )}
                    </Stack>
                  ))}
                </Stack>
                <Stack direction={'row'} gap={1} alignItems={'center'}>
                  <ActionIcon color={'action'} action={Action.Sms} fontSize={'small'} />
                  {usedFields.sms.map((sms, index) => (
                    <Stack key={index} alignItems={'center'}>
                      {sms.includes(`[${field.name}]`) ? (
                        <Check fontSize={'small'} color={'success'} />
                      ) : (
                        <Clear fontSize={'small'} color={'error'} />
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : null}

        {fieldsData?.length === 0 && <Typography color={'text.secondary'}>{t('empty')}</Typography>}
      </Box>
    </Paper>
  );
};
